import { n as printDocument, r as printGeometryVariables, t as applyPrintGeometry } from "./printDocument-F_WrOoo7.mjs";
import { useState } from "react";
import { createPageGeometry, isPrinted, pageBackgroundCss, parseAssetUri, toDocx, toHtml, toMarkdown, toPlainText } from "@burfy/docs-core";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/export/ExportDialog.tsx
/**
* The formats, with an honest description of each.
*
* Two different guarantees are on offer here and conflating them is the usual
* way products mislead: PDF is a *rendering* of our layout and matches the
* screen exactly; DOCX and the text formats are *translations* of the model,
* where headings stay headings but the page breaks are Word's own. Saying so
* at the point of choosing is part of the feature.
*/
const FORMATS = [
	{
		value: "pdf",
		label: "PDF — via your browser’s print dialog",
		viaPrintDialog: true,
		fidelity: "Exactly the pages you see, with selectable text. Your browser’s print dialog opens — choose “Save as PDF” as the destination. The browser does the typesetting, which is what keeps Malayalam and other Indic text shaped correctly."
	},
	{
		value: "docx",
		label: "Word (.docx)",
		fidelity: "Editable in Word. Word recalculates page breaks with its own layout engine, so use PDF when the exact page layout matters."
	},
	{
		value: "html",
		label: "Web page (.html)",
		fidelity: "Self-contained, with images embedded. Good for email and CMS paste."
	},
	{
		value: "markdown",
		label: "Markdown (.md)",
		fidelity: "Headings, emphasis, lists, links and tables. Text wrap, colours, fonts and page setup are not represented."
	},
	{
		value: "text",
		label: "Plain text (.txt)",
		fidelity: "Words only. All formatting is dropped."
	}
];
function ExportDialog({ onExport, onCancel, busy = false }) {
	const [format, setFormat] = useState("docx");
	const selected = FORMATS.find((entry) => entry.value === format);
	const action = selected?.viaPrintDialog ? "Open print dialog" : "Download";
	return /* @__PURE__ */ jsx("div", {
		className: "burfy-docs-dialog-backdrop",
		onClick: onCancel,
		role: "presentation",
		children: /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-dialog",
			role: "dialog",
			"aria-modal": "true",
			"aria-label": "Export document",
			onClick: (event) => event.stopPropagation(),
			children: [
				/* @__PURE__ */ jsx("h2", {
					className: "burfy-docs-dialog-title",
					children: "Export"
				}),
				/* @__PURE__ */ jsxs("fieldset", {
					className: "burfy-docs-fieldset",
					children: [/* @__PURE__ */ jsx("legend", { children: "Format" }), FORMATS.map((entry) => /* @__PURE__ */ jsxs("label", {
						className: "burfy-docs-radio",
						children: [/* @__PURE__ */ jsx("input", {
							type: "radio",
							name: "export-format",
							value: entry.value,
							checked: format === entry.value,
							onChange: () => setFormat(entry.value)
						}), /* @__PURE__ */ jsx("span", { children: entry.label })]
					}, entry.value))]
				}),
				/* @__PURE__ */ jsx("p", {
					className: "burfy-docs-dialog-note",
					children: selected?.fidelity
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-dialog-actions",
					children: [
						/* @__PURE__ */ jsx("span", { className: "burfy-docs-dialog-spacer" }),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							onClick: onCancel,
							children: "Cancel"
						}),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							className: "burfy-docs-dialog-primary",
							disabled: busy,
							onClick: () => onExport(format),
							children: busy ? "Preparing…" : action
						})
					]
				})
			]
		})
	});
}
//#endregion
//#region src/export/download.ts
/**
* Saving an exported file.
*
* A single place that creates an object URL, clicks a link and revokes the
* URL — the revoke is the part that gets forgotten, and a forgotten one pins
* the whole exported file in memory for the life of the session.
*/
const EXPORT_MIME = {
	html: "text/html;charset=utf-8",
	markdown: "text/markdown;charset=utf-8",
	text: "text/plain;charset=utf-8",
	docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
};
/**
* Turn a document title into a file name.
*
* The result reaches a file system, so path separators and the characters
* Windows refuses have to go. An untitled document still gets a name — a file
* called `.docx` is one nobody can find again.
*/
/** Control characters, and the punctuation file systems refuse. */
const UNSAFE_FILENAME_CHARS = new RegExp(`[\\u0000-\\u001f<>:"/\\\\|?*]`, "g");
function toFileName(title, extension) {
	return `${title.replace(UNSAFE_FILENAME_CHARS, " ").replace(/\s+/g, " ").trim().slice(0, 100) || "Untitled document"}.${extension}`;
}
function downloadFile(data, fileName, mime) {
	if (typeof document === "undefined") return;
	const blob = typeof data === "string" ? new Blob([data], { type: mime }) : new Blob([data], { type: mime });
	const url = URL.createObjectURL(blob);
	try {
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		link.rel = "noopener";
		document.body.append(link);
		link.click();
		link.remove();
	} finally {
		setTimeout(() => URL.revokeObjectURL(url), 1e4);
	}
}
//#endregion
//#region src/export/exportDocument.ts
/** Read every asset the document references, once. */
async function loadAssets(document, store) {
	const assets = /* @__PURE__ */ new Map();
	if (!store) return assets;
	await Promise.all(Object.keys(document.assets).map(async (id) => {
		const stored = await store.get(id).catch(() => null);
		if (stored) assets.set(id, {
			bytes: stored.bytes,
			mime: stored.ref.mime
		});
	}));
	return assets;
}
function toDataUri(bytes, mime) {
	let binary = "";
	const chunk = 32768;
	for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
	return `data:${mime};base64,${btoa(binary)}`;
}
async function exportDocument({ document, format, assetStore }) {
	if (format === "pdf") {
		const background = document.meta.pageSetup.background;
		const printed = background && isPrinted(background);
		const assetId = printed && background.kind === "image" ? parseAssetUri(background.src) : null;
		const url = assetId && assetStore ? await assetStore.resolveUrl(assetId) : null;
		try {
			printDocument(createPageGeometry(document.meta.pageSetup), { background: {
				css: printed ? pageBackgroundCss(background, url) : null,
				...printed && background.kind === "image" ? { opacity: background.opacity } : {}
			} });
		} finally {
			if (assetId && assetStore) assetStore.release(assetId);
		}
		return;
	}
	const title = document.meta.title;
	if (format === "docx") {
		const assets = await loadAssets(document, assetStore);
		downloadFile(await toDocx(document, { resolveImage: (id) => assets.get(id)?.bytes ?? null }), toFileName(title, "docx"), EXPORT_MIME.docx);
		return;
	}
	if (format === "html") {
		const assets = await loadAssets(document, assetStore);
		downloadFile(toHtml(document, {
			standalone: true,
			resolveImage: (id) => {
				const asset = assets.get(id);
				return asset ? toDataUri(asset.bytes, asset.mime) : null;
			}
		}), toFileName(title, "html"), EXPORT_MIME.html);
		return;
	}
	if (format === "markdown") {
		downloadFile(toMarkdown(document), toFileName(title, "md"), EXPORT_MIME.markdown);
		return;
	}
	downloadFile(toPlainText(document), toFileName(title, "txt"), EXPORT_MIME.text);
}
//#endregion
export { EXPORT_MIME, ExportDialog, applyPrintGeometry, downloadFile, exportDocument, printDocument, printGeometryVariables, toFileName };
